import { uvToSphereGlsl, applyInverseWarpGlsl } from './glslUtils';

/** Envelope shader — batched SDF capsule renderer for smooth stroke envelopes.
 *
 *  Renders up to `batchSize` capsule segments (uSegA → uSegB) per draw call.
 *  Uses GLSL 3.00 ES for fwidth-based sub-pixel antialiasing and explicit
 *  out fragColor (no gl_FragColor).
 *
 *  Two projection modes (uProjMode uniform):
 *  - 0 (wrap):    capsule endpoints are world-space unit-sphere points;
 *                 SDF is computed in 3-D.
 *  - 1 (project): endpoints are NDC; SDF is computed in 2-D screen space.
 *
 *  Output is premultiplied RGBA so that bilinear edge blending is correct
 *  when the result is later composited with the composite shader.
 *
 *  NOTE: Because batchSize is baked into array-size literals and the for-loop
 *  bound, the shaders must be rebuilt when the batch size changes. Call
 *  `makeEnvelopeShaders(batchSize)` to obtain a matched { vertexShader,
 *  fragmentShader } pair; cache the result when batchSize is constant.
 */

export function makeEnvelopeShaders(batchSize: number): {
  vertexShader: string;
  fragmentShader: string;
} {
  // Three.js injects 'in vec3 position' and 'in vec2 uv' automatically for GLSL3.
  const vertexShader = /*glsl*/`
    out vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = vec4(position, 1.0);
    }
  `;

  const fragmentShader = /*glsl*/`
    precision highp float;
    in vec2 vUv;
    out vec4 fragColor;

    uniform sampler2D uPrev;
    uniform vec3  uBrushColor;
    uniform int   uProjMode;
    uniform mat4  uCamViewProj;
    uniform vec3  uCamPos;
    uniform float uViewAspect;
    uniform vec2  uSphereCenterNDC;
    uniform float uInverseWarpK;
    uniform float uInverseWarpStrength;
    uniform int   uCount;
    uniform vec4  uSegA[${batchSize}];
    uniform vec4  uSegB[${batchSize}];

${uvToSphereGlsl}
    float capsule3D(vec3 p, vec3 a, vec3 b, float ra, float rb) {
      vec3 ab = b - a;
      float lenSq = dot(ab, ab);
      float t = lenSq > 1e-8 ? clamp(dot(p - a, ab) / lenSq, 0.0, 1.0) : 0.0;
      return distance(p, a + t * ab) - mix(ra, rb, t);
    }

    float capsule2D(vec2 p, vec2 a, vec2 b, float ra, float rb) {
      vec2 ab = b - a;
      float lenSq = dot(ab, ab);
      float t = lenSq > 1e-8 ? clamp(dot(p - a, ab) / lenSq, 0.0, 1.0) : 0.0;
      return distance(p, a + t * ab) - mix(ra, rb, t);
    }

${applyInverseWarpGlsl}
    void main() {
      vec4 prev = texture(uPrev, vUv);
      vec3 pos  = uvToSphere(vUv);
      float minSDF = 1e6;

      if (uProjMode == 1 || uProjMode == 2) {
        if (dot(pos, uCamPos) <= 0.0) { fragColor = prev; return; }
        vec2 ndc;
        if (uProjMode == 2) {
          float camDist2 = dot(uCamPos, uCamPos);
          vec3 rayFrag = pos - uCamPos;
          float tBack = (camDist2 - 1.0) / dot(rayFrag, rayFrag);
          if (tBack <= 0.0) { fragColor = prev; return; }
          vec3 pBack = uCamPos + tBack * rayFrag;
          vec4 clip = uCamViewProj * vec4(pBack, 1.0);
          if (clip.w <= 0.0) { fragColor = prev; return; }
          ndc = clip.xy / clip.w;
          ndc = applyInverseWarp(ndc);
        } else {
          vec4 clip = uCamViewProj * vec4(pos, 1.0);
          if (clip.w <= 0.0) { fragColor = prev; return; }
          ndc = clip.xy / clip.w;
        }
        vec2 p = vec2(ndc.x * uViewAspect, ndc.y);
        for (int i = 0; i < ${batchSize}; i++) {
          if (i >= uCount) break;
          vec2 a = vec2(uSegA[i].x * uViewAspect, uSegA[i].y);
          vec2 b = vec2(uSegB[i].x * uViewAspect, uSegB[i].y);
          minSDF = min(minSDF, capsule2D(p, a, b, uSegA[i].z, uSegB[i].z));
        }
      } else {
        for (int i = 0; i < ${batchSize}; i++) {
          if (i >= uCount) break;
          minSDF = min(minSDF, capsule3D(pos, uSegA[i].xyz, uSegB[i].xyz, uSegA[i].w, uSegB[i].w));
        }
      }

      // Sub-pixel AA: fwidth gives the SDF gradient magnitude across one
      // texel, so smoothstep over [−half, +half] produces exactly 1-texel
      // soft edge without any hardcoded pixel-size constant.
      float aaWidth  = fwidth(minSDF);
      float mask     = 1.0 - smoothstep(-aaWidth * 0.5, aaWidth * 0.5, minSDF);
      float outAlpha = max(prev.a, mask);
      // Premultiplied RGB keeps bilinear edge blending correct.
      vec3  outColor = (mask > prev.a) ? uBrushColor * mask : prev.rgb;
      fragColor      = vec4(outColor, outAlpha);
    }
  `;

  return { vertexShader, fragmentShader };
}
